// Update your AuthEnforcer to handle permission checks
// components/auth-enforcer.tsx

'use client';

import { useAuth } from '../lib/auth';
import { usePermissions } from '../lib/permission-context';
import { DomainAwareLogin } from './auth/domain-aware-login';
import TenantSelector from './tenant-selector';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import TenantEnrollment from './tenant-enrollment/tenant-enrollment';
import { AppLayout } from './app-layout';
import { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useToast } from '@/components/ui/use-toast';
import React from 'react';
import { usePreserveLocation } from '@/hooks/use-preserve-location';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getAccentClasses, getHoverClasses } from '@/lib/theme-utils';

/** Preserve query/hash when replacing pathname (e.g. `/dashboard/print?pdfSid=…` → slug URL). */
function browserSearchAndHash(): string {
  if (typeof window === 'undefined') return ''
  return (window.location.search || '') + (window.location.hash || '')
}

// Define route permissions
const ROUTE_PERMISSIONS: Record<string, { resource: string; action: string }> = {
  '/mdr': { resource: 'cases', action: 'read' },
  '/security': { resource: 'security', action: 'read' },
  '/admin': { resource: 'admin', action: 'access' },
  // Add more routes as needed
};

const TENANT_PICKER_THEME = 'tenant-selector';

export const AuthEnforcer = React.memo(function AuthEnforcer({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useTheme();
  const themeBeforeTenantPickerRef = useRef<string | null>(null);
  
  const { 
    user, 
    session,
    availableTenants,
    currentTenant,
    needsTenantSelection,
    needsEnrollment,
    isLoading, 
    error,
    authPhase,
    selectTenant,
    switchTenant,
    lastTenantSwitchAt
  } = useAuth();

  const { hasPermission } = usePermissions();
  const pathname = usePathname();
  const router = useRouter();
  const { toast } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  /** Avoid SSR/client hydration mismatch before localStorage-backed E2E auth is applied. */
  const [authShellHydrated, setAuthShellHydrated] = useState(false);
  const { restoreLocation, getCurrentStoredLocation, validateAndCleanStoredLocation, markUserNavigatedAway, clearStoredLocation } = usePreserveLocation();

  useLayoutEffect(() => {
    setAuthShellHydrated(true);
  }, []);

  const isMarketplaceE2e =
    authShellHydrated &&
    typeof window !== 'undefined' &&
    localStorage.getItem('e2e_skip_session_websocket') === '1';

  const isE2eSessionReady = useMemo(() => {
    if (!isMarketplaceE2e || typeof window === 'undefined') return false;
    if (session?.sessionId && currentTenant?.tenantId) return true;
    try {
      const storedSession = localStorage.getItem('session');
      const storedTenant = localStorage.getItem('currentTenant');
      if (!storedSession?.trim() || !storedTenant?.trim()) return false;
      const parsedSession = JSON.parse(storedSession) as { sessionId?: string };
      const parsedTenant = JSON.parse(storedTenant) as { tenantId?: string };
      return !!parsedSession.sessionId && !!parsedTenant.tenantId;
    } catch {
      return false;
    }
  }, [isMarketplaceE2e, session?.sessionId, currentTenant?.tenantId]);
  
  // Track redirect attempts to prevent infinite loops
  const redirectAttemptsRef = useRef<{ count: number; lastAttempt: number; lastPath: string }>({ count: 0, lastAttempt: 0, lastPath: '' });

  // Only canonicalize slug for these top-level app routes to reduce churn
  const CANONICALIZE_PREFIXES = useMemo(() => [
    '/',
    '/dashboard',
    '/mdr',
    '/security',
    '/admin',
    '/settings',
    '/integrations',
  ], []);

  const isCanonicalizablePath = useCallback((p: string) => {
    if (!p) return false;
    // Exact root '/' or any path that starts with one of the prefixes
    if (p === '/') return true;
    return CANONICALIZE_PREFIXES.some(prefix => prefix !== '/' && p.startsWith(prefix + (prefix === '/' ? '' : '')));
  }, [CANONICALIZE_PREFIXES]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const showTenantPicker =
    !isMarketplaceE2e &&
    !!session &&
    needsTenantSelection &&
    (availableTenants?.length ?? 0) > 0;

  // Apply dedicated picker theme while modal is open; restore user's theme when it closes.
  // Do not persist tenant-selector — it is ephemeral UI chrome, not a user preference.
  useEffect(() => {
    if (!showTenantPicker) {
      const restore = themeBeforeTenantPickerRef.current;
      if (restore) {
        setTheme(restore);
        themeBeforeTenantPickerRef.current = null;
      }
      return;
    }

    if (themeBeforeTenantPickerRef.current === null) {
      // If storage was corrupted to tenant-selector, restore to dark instead of looping on it
      const prior = theme === TENANT_PICKER_THEME ? 'dark' : theme;
      themeBeforeTenantPickerRef.current = prior;
      if (theme !== TENANT_PICKER_THEME) {
        setTheme(TENANT_PICKER_THEME, { persist: false });
      }
    }
  }, [showTenantPicker, theme, setTheme]);

  // Detect and fix malformed URLs (e.g., duplicate /t/ slugs)
  useEffect(() => {
    // Key off authPhase (not isLoading/error) so we don't disagree with render branching.
    if (authPhase !== 'authenticated' || !currentTenant?.tenantSlug) return;

    const path = pathname || '/'
    if (path.includes('/dashboard/print')) return

    // Check for duplicate /t/ slugs in the current pathname
    const tMatches = path.match(/\/t\//g);
    if (tMatches && tMatches.length > 1) {
      console.warn(' [AUTH-ENFORCER] Detected malformed URL with duplicate /t/ slugs:', path);
      
      // Clear any stored location that might be causing this
      clearStoredLocation?.();
      
      // Extract the last valid slug and path, then redirect to correct URL
      const parts = path.split('/t/').filter(p => p);
      const lastPart = parts[parts.length - 1];
      const lastParts = lastPart.split('/');
      const slug = lastParts[0];
      const rest = '/' + lastParts.slice(1).join('/');
      const correctedPath = `/t/${slug}${rest === '/' ? '/dashboard' : rest}`;
      
      console.log(' [AUTH-ENFORCER] Redirecting to corrected URL:', correctedPath);
      router.replace(correctedPath + browserSearchAndHash());
      return;
    }
  }, [pathname, authPhase, currentTenant?.tenantSlug, router, clearStoredLocation]);

  // Slug-based routing guard and canonicalization
  useEffect(() => {
    // Must match render: computeAuthPhase returns 'authenticated' from user+session+tenant
    // even when a stale `error` or brief `isLoading` remains. Gating on those left users
    // stuck on `/` (HomePage returns null while waiting for this redirect).
    if (authPhase !== 'authenticated') return;
    if (!session?.sessionId || !currentTenant?.tenantSlug) return;
    if (needsEnrollment) return;
    // Do not gate on needsTenantSelection when currentTenant is already bound —
    // that combination used to leave `/` blank (AppLayout + HomePage null, no redirect).

    const path = pathname || '/'
    // Skip public paths
    if (path.startsWith('/auth') || path.startsWith('/enroll') || path.startsWith('/enrollment-success')) return
    if (path.includes('/dashboard/print')) return

    // CRITICAL: Always check the actual browser URL first to prevent loops
    // The middleware rewrites /t/{slug}/dashboard to /dashboard internally,
    // but window.location.pathname shows the actual URL in the browser
    const actualUrl = typeof window !== 'undefined' ? window.location.pathname : pathname
    if (actualUrl.includes('/dashboard/print')) return

    const slugPrefix = '/t/';
    const hasSlugInActualUrl = actualUrl.startsWith(slugPrefix);

    if (hasSlugInActualUrl) {
      // URL already has slug prefix - check if it matches current tenant
      const parts = actualUrl.split('/');
      // ['', 't', '{slug}', ...rest]
      const slug = parts[2] || '';
      const rest = '/' + parts.slice(3).join('/');

      // If we're already at the correct slug path, don't do anything
      if (slug === currentTenant.tenantSlug) {
        // Path is correct, no action needed
        return;
      }

      // Slug mismatch - need to switch tenant or redirect
      if (slug && currentTenant.tenantSlug && slug !== currentTenant.tenantSlug) {
        // Debounce slug-triggered switching to avoid double-switch: ignore if within 750ms of a programmatic switch
        const now = Date.now();
        if (lastTenantSwitchAt && now - lastTenantSwitchAt < 750) {
          return;
        }
        // If the slug matches one of available tenants and differs from current, switch using switchTenant (idempotent + guarded)
        const target = availableTenants.find(t => t.tenantSlug === slug);
        if (target && target.tenantId && target.tenantId !== currentTenant.tenantId) {
          (async () => {
            try {
              toast({ title: 'Switching organization…', description: target.tenantName || target.tenantSlug });
            } catch {}
            await switchTenant(target.tenantId!);
            // No immediate replace here; allow app-layout to canonicalize post-switch
          })();
        } else if (!target) {
          // Not authorized for this slug; redirect to current only if not already there
          const targetPath = `/t/${currentTenant.tenantSlug}${rest === '/' ? '/dashboard' : rest}`;
          if (actualUrl !== targetPath) {
            router.replace(targetPath + browserSearchAndHash());
          }
        }
      }
      return; // Exit early - we're already handling slug-prefixed URLs
    }

    // No slug in actual URL - need to canonicalize
    // Only canonicalize for approved routes to avoid route churn
    if (!isCanonicalizablePath(path)) return;
    
    // Check redirect attempt counter to prevent infinite loops
    const now = Date.now();
    const targetPath = `/t/${currentTenant.tenantSlug}${path === '/' ? '/dashboard' : path}`;
    
    // Reset counter if path changed or enough time has passed (5 seconds)
    if (redirectAttemptsRef.current.lastPath !== targetPath || (now - redirectAttemptsRef.current.lastAttempt) > 5000) {
      redirectAttemptsRef.current = { count: 0, lastAttempt: now, lastPath: targetPath };
    }
    
    // Prevent redirect if we've attempted too many times (max 2 attempts to be more conservative)
    if (redirectAttemptsRef.current.count >= 2) {
      console.warn(' [AUTH-ENFORCER] Too many redirect attempts, stopping to prevent loop');
      clearStoredLocation?.();
      redirectAttemptsRef.current = { count: 0, lastAttempt: now, lastPath: '' };
      return;
    }
    
    // Validate and clean stored location before using it
    const isValidStoredLocation = validateAndCleanStoredLocation?.() || false;
    
    // Prefer restoring the last stored location when path is '/' to avoid dumping user to dashboard
    // But only if it's valid and doesn't already contain a slug prefix
    // CRITICAL: Only use stored location if validation passed to prevent stuck states
    const last = isValidStoredLocation ? (getCurrentStoredLocation?.() || null) : null;
    let rest = path === '/' ? (last || '/dashboard') : path;
    
    // If stored location already has a slug prefix, extract just the path part
    if (last && last.startsWith('/t/')) {
      const lastParts = last.split('/');
      if (lastParts.length >= 4) {
        const extractedPath = '/' + lastParts.slice(3).join('/');
        // Validate the extracted path is a valid route
        if (isCanonicalizablePath(extractedPath)) {
          rest = extractedPath;
        } else {
          // Invalid extracted path, fallback to dashboard
          rest = '/dashboard';
          // Clear the invalid stored location
          clearStoredLocation?.();
        }
      } else {
        rest = '/dashboard'; // Fallback if malformed
        // Clear the malformed stored location
        clearStoredLocation?.();
      }
    } else if (last && !isCanonicalizablePath(last)) {
      // Stored location is not a valid canonicalizable path, use dashboard instead
      rest = '/dashboard';
      // Clear the invalid stored location
      clearStoredLocation?.();
    }
    
    const suffix = browserSearchAndHash()
    const pathOnlyFinal = `/t/${currentTenant.tenantSlug}${rest}`
    const finalPath = `${pathOnlyFinal}${suffix}`
    const actualComparable = `${actualUrl}${suffix}`
    // Only redirect if we're not already at the target path (check actual URL, not pathname)
    // Add additional check: make sure we're not in a redirect loop
    if (actualComparable !== finalPath && redirectAttemptsRef.current.count < 2) {
      // Increment redirect attempt counter
      redirectAttemptsRef.current.count++;
      redirectAttemptsRef.current.lastAttempt = now;
      redirectAttemptsRef.current.lastPath = pathOnlyFinal;
      router.replace(finalPath);
    }
  }, [authPhase, session?.sessionId, currentTenant, availableTenants, pathname, needsEnrollment, router, toast, switchTenant, isCanonicalizablePath, lastTenantSwitchAt, clearStoredLocation, validateAndCleanStoredLocation, getCurrentStoredLocation]);

  // After auth/session settles, try restoring the last location if we were bounced to dashboard
  // But only once and only if the stored location is valid
  // DISABLED: This effect is disabled because restoreLocation always returns false and the redirect
  // is already handled in the canonicalization logic above. Keeping this disabled prevents duplicate redirects.
  useEffect(() => {
    // Disabled to prevent duplicate redirects - redirect is handled in canonicalization logic
    // The restoreLocation function always returns false anyway (location restoration is disabled)
    return;
    
    // Original code (disabled):
    // if (!session || !session.sessionId || !currentTenant || isLoading || error) return;
    // if (!validateAndCleanStoredLocation?.()) return;
    // const slugPrefix = '/t/';
    // const path = pathname || '/';
    // const atDefault = path === '/' || path === '/dashboard' || (path.startsWith(slugPrefix) && (path === `${slugPrefix}${currentTenant.tenantSlug}` || path === `${slugPrefix}${currentTenant.tenantSlug}/dashboard`));
    // if (atDefault) {
    //   const restored = restoreLocation?.();
    //   if (restored) {
    //     const handlePopState = () => { markUserNavigatedAway?.(); };
    //     window.addEventListener('popstate', handlePopState, { once: true });
    //     setTimeout(() => { window.removeEventListener('popstate', handlePopState); }, 3000);
    //   }
    // }
  }, [session?.sessionId, currentTenant?.tenantSlug, pathname, isLoading, error, restoreLocation, validateAndCleanStoredLocation, markUserNavigatedAway]);

  // Debug: Log the actual session object structure (can remove in production)
  useEffect(() => {
    if (session) {
      console.log(' [AUTH-ENFORCER] Session object debug:', {
        hasSession: !!session,
        sessionId: session.sessionId,
        sessionKeys: Object.keys(session),
        needsEnrollment,
        needsTenantSelection,
        currentTenant: currentTenant?.tenantId
      });
    }
  }, [session, needsEnrollment, needsTenantSelection, currentTenant]);
  
  // Debug current state - only log when state actually changes
  useEffect(() => {
    console.log(' [AUTH-ENFORCER] Auth state:', { 
      user: !!user, 
      session: !!session,
      sessionId: session?.sessionId,
      currentTenant: currentTenant?.tenantId,
      availableTenantsCount: availableTenants?.length || 0,
      needsTenantSelection, 
      needsEnrollment, 
      isLoading, 
      error: !!error,
      initialLoading,
      pathname
    });
  }, [user, session, currentTenant, availableTenants, needsTenantSelection, needsEnrollment, isLoading, error, initialLoading, pathname]);

  /**
   * Headless PDF: Playwright injects localStorage/session + snapshot but does not load an OIDC `user`.
   * `computeAuthPhase` therefore never reaches `authenticated`, so we must not block on the global
   * loading gate or slug canonicalization (which can fight middleware and leave the app off-route).
   */
  const pathForAuth = pathname || ''
  const isSecurityDashboardPdfPrint = pathForAuth.includes('/dashboard/print')

  const authLoadingShell = (
    <div className={`flex min-h-screen items-center justify-center ${getBackgroundClasses(theme, 'page')}`}>
      <div className="text-center">
        <div className="relative w-16 h-16 mx-auto mb-6">
          <div className="absolute inset-0 border-4 rounded-full /30 animate-ping"></div>
          <div className="absolute border-4 rounded-full inset-2 border-t-cyan-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
        </div>
        <h1 className={`text-2xl font-bold mb-4 ${getTextClasses(theme)}`}>Loading...</h1>
        <p className={getTextClasses(theme, 'muted')}>Checking your authentication status</p>
      </div>
    </div>
  );

  if (!authShellHydrated) {
    return authLoadingShell;
  }

  // Playwright E2E: bypass authPhase loading once React session state is hydrated from localStorage.
  if (isE2eSessionReady) {
    return (
      <AppLayout>
        {children}
      </AppLayout>
    );
  }

  if (isSecurityDashboardPdfPrint) {
    return (
      <div
        className={`flex w-full flex-1 flex-col overflow-visible min-h-min ${getBackgroundClasses(theme, 'page')}`}
      >
        {children}
      </div>
    )
  }

  // Use authPhase for reliable branching (see AUTH_DESIGN_AND_HARDENING.md)
  if (initialLoading || authPhase === 'loading' || authPhase === 'callback') {
    return authLoadingShell;
  }

  if (authPhase === 'expired') {
    return <DomainAwareLogin sessionMessage={error?.message ?? 'Your session has expired. Please sign in again.'} />;
  }

  if (authPhase === 'error') {
    return (
      <div className={`flex min-h-screen items-center justify-center ${getBackgroundClasses(theme, 'page')}`}>
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} backdrop-blur-sm rounded-xl shadow-xl max-w-md w-full p-8 border`}>
          <div className="text-center">
            <h1 className={`text-2xl font-bold mb-4 ${getTextClasses(theme)}`}>Authentication Error</h1>
            <p className={`${getTextClasses(theme, 'muted')} mb-6`}>{error?.message ?? 'Something went wrong.'}</p>
            <button
              onClick={() => {
                localStorage.removeItem('session');
                localStorage.removeItem('authToken');
                localStorage.removeItem('availableTenants');
                localStorage.removeItem('currentTenant');
                window.location.reload();
              }}
              className={`px-4 py-2 ${getAccentClasses(theme, 'primary')} ${getTextClasses(theme)} rounded-lg ${getHoverClasses(theme)} transition-colors`}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Aegis session without OIDC `user` is NOT broken. Hard refresh restores session +
  // Aegis Bearer from storage before oidc-client `getUser()` resolves (or if OIDC user
  // is briefly unavailable). Clearing here forced DomainAwareLogin despite a valid
  // validateSession — see refresh_logs_out_user.har. API auth is the Aegis token;
  // fall through to authPhase === 'authenticated' / tenant picker below.

  // Tenant picker must win over the authenticated AppLayout branch. Otherwise
  // needsTenantSelection + currentTenant renders a blank `/` (HomePage returns null)
  // and never shows the modal.
  if (session && showTenantPicker) {
    return (
      <Dialog open data-testid="tenant-picker-modal">
        <DialogContent
          className={`max-w-3xl ${getBackgroundClasses(theme, 'popover')} ${getBorderClasses(theme)}`}
          data-testid="tenant-picker-content"
        >
          <DialogHeader>
            <DialogTitle
              className={`flex items-center gap-2 ${getTextClasses(theme)}`}
              data-testid="tenant-picker-title"
            >
              Select Organization
            </DialogTitle>
            <DialogDescription className={getTextClasses(theme, 'muted')}>
              You have access to multiple organizations. Choose the one you want to work in.
            </DialogDescription>
          </DialogHeader>
          <TenantSelector
            availableTenants={availableTenants}
            onSelect={async (tenantId) => {
              try {
                const chosen = availableTenants.find((t) => t.tenantId === tenantId);
                toast({
                  title: 'Switching organization…',
                  description: chosen?.tenantName || chosen?.tenantSlug || tenantId,
                });
              } catch {
                /* toast is best-effort */
              }
              await selectTenant(tenantId);
              const chosen = availableTenants.find((t) => t.tenantId === tenantId);
              const slug = chosen?.tenantSlug || currentTenant?.tenantSlug;
              if (slug) {
                router.push(`/t/${slug}/dashboard`);
              }
            }}
            userEmail={session.profile?.email || user?.profile?.email || 'Unknown'}
            isLoading={isLoading}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (authPhase === 'authenticated') {
    console.log(' [AUTH-ENFORCER] Valid session with current tenant and user - checking permissions...');

    // Check if current route requires specific permissions
    const requiredPermission = ROUTE_PERMISSIONS[pathname];
    
    if (requiredPermission) {
      const userHasPermission = hasPermission(requiredPermission.resource, requiredPermission.action);
      
      if (!userHasPermission) {
        console.log(' [AUTH-ENFORCER] Access denied for route:', pathname, 'missing permission:', requiredPermission);
        
        // Show access restricted page WITHOUT AppLayout wrapper
        return (
          <div className={`flex min-h-screen items-center justify-center ${getBackgroundClasses(theme, 'page')}`}>
            <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg shadow-xl p-8 max-w-md w-full text-center border`}>
              <h2 className={`text-xl font-semibold ${getTextClasses(theme)} mb-4`}>Access Restricted</h2>
              <p className={`${getTextClasses(theme, 'muted')} mb-6`}>
                You do not have permission to access this page.
              </p>
              <p className={`text-sm ${getTextClasses(theme, 'subtle')} mb-4`}>
                Required permission: <code className={`${getBackgroundClasses(theme, 'popover')} px-2 py-1 rounded`}>{requiredPermission.resource}:{requiredPermission.action}</code>
              </p>
              <button
                onClick={() => window.history.back()}
                className={`px-4 py-2 ${getAccentClasses(theme, 'primary')} ${getTextClasses(theme)} rounded-lg ${getHoverClasses(theme)} transition-colors`}
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
    }
    
    // User has permission (or no permission required) - show app with AppLayout
    console.log(' [AUTH-ENFORCER] Permission check passed, showing authenticated app');
    return (
      <AppLayout>
        {children}
      </AppLayout>
    );
  }
  
  // 2. Handle other auth states (enrollment, etc.)
  if (session) {
    if (needsEnrollment) {
      return (
        <TenantEnrollment prefillDomain={session.profile?.email?.split('@')[1] || ''} />
      );
    }
    
    // **CRITICAL FIX**: If we have a session but no sessionId AND no tenant, 
    // this means we're in an enrollment state that hasn't been properly flagged
    if (!session.sessionId && !currentTenant && !needsTenantSelection) {
      console.log(' [AUTH-ENFORCER] Session without sessionId or tenant - showing enrollment');
      return (
        <TenantEnrollment prefillDomain={session.profile?.email?.split('@')[1] || user?.profile?.email?.split('@')[1] || ''} />
      );
    }
  }
  
  if (user && !session) {
    return (
      <div className={`flex min-h-screen items-center justify-center ${getBackgroundClasses(theme, 'page')}`}>
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 rounded-full /30 animate-ping"></div>
            <div className="absolute border-4 rounded-full inset-2 border-t-cyan-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
          </div>
          <h1 className={`text-2xl font-bold mb-4 ${getTextClasses(theme)}`}>Creating your session...</h1>
          <p className={getTextClasses(theme, 'muted')}>Please wait while we set up your access</p>
        </div>
      </div>
    );
  }
  
  // No authentication found - show login
  console.log(' [AUTH-ENFORCER] No authentication found, showing login');
  return <DomainAwareLogin />;
});